/**
 * EmailVerificationGate — porta de identidade antes do onboarding.
 *
 * ## Por que existe (incidente de 30/08/2026, medido)
 *
 * `createUserWithEmailAndPassword` aceita QUALQUER string como e-mail e o
 * Firebase não a verifica sozinho. Entre 26 e 30/08 entraram 76 contas com
 * endereços inventados — `livrados17877645530@gmail.com`,
 * `retencao.cliente.<ts>@gmail.com`, `*@livroboxmail.com` (domínio que nem
 * existe: NXDOMAIN, sem MX) — criadas quatro por segundo por script. Uma delas
 * creditou 2.500.000 moedas pela `awardCoins`.
 *
 * ## Esta tela NÃO é o gate — ela é a UX do gate
 *
 * Quem barra de verdade é a rule `hasVerifiedEmail()` no fecho do onboarding:
 * o atacante não usa a interface, ele fala com a API direto, então um guard só
 * de client não o alcançaria. O que esta tela faz é impedir que a pessoa
 * LEGÍTIMA descubra o bloqueio no último passo do wizard, depois de escolher
 * tema, gêneros e favoritos — e é por isso que ela vem ANTES dele.
 *
 * ## Alcance
 *
 * Google e Apple entregam `emailVerified: true` (medido em produção: 35/35 e
 * 7/7), então na prática isto só aparece para o provider `password`. Quem já
 * concluiu o onboarding nunca passa por aqui — o grandfathering dos leitores
 * que entraram por senha antes desta data é automático.
 *
 * ## As duas saídas são obrigatórias, não cortesia
 *
 * "Reenviar" existe porque e-mail cai em spam e endereço se digita errado;
 * "entrar com outra conta" existe porque o caso mais provável de alguém
 * legítimo travar aqui é ter se cadastrado com o endereço errado. Sem elas,
 * esta tela seria terminal — a classe de defeito que o BUG-93 custou caro
 * (quem caía na fila do beta não tinha como sair) e que o BUG-58 registra.
 */

import { useEffect, useState, type ReactElement } from "react";
import { getAuth, signOut } from "firebase/auth";
import { MailCheck } from "lucide-react";
import { Button } from "../components/ui";
import { TextButton } from "../components/ui/TextButton";
import { useAuth } from "../hooks/useAuth";
import {
  refreshEmailVerification,
  resendVerificationEmail,
} from "../services/firebase/authService";

interface EmailVerificationGateProps {
  children: ReactElement;
}

export function EmailVerificationGate({ children }: EmailVerificationGateProps) {
  const { user, isLoading } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [stillUnverified, setStillUnverified] = useState(false);

  // Re-checa sozinho quando a pessoa VOLTA para cá (BUG-264). O link do e-mail
  // abre em outra aba/app; ao retornar, `user.emailVerified` ainda é o espelho
  // local e a tela seguiria dizendo "Confirme seu e-mail" até alguém apertar
  // "Já confirmei". Silencioso de propósito: se ainda não verificou, nada
  // muda na tela — a mensagem "ainda não" é só do clique explícito.
  //
  // ⚠️ O `bump` não é enfeite: `reload()` notifica o `onAuthStateChanged` com
  // o MESMO objeto `User` (mutado no lugar — medido no SDK, `reload` →
  // `_notifyListenersIfCurrent(userInternal)`), e `setUser(mesmaRef)` no
  // AuthContext não re-renderiza nada. O botão só funciona porque o
  // `setChecking(false)` dele re-renderiza esta tela; aqui é o `bump` que faz
  // o `user.emailVerified` novo ser lido.
  // Precisa vir ANTES dos early returns (rules-of-hooks).
  const [, bump] = useState(0);
  const needsCheck = !isLoading && !!user && !user.emailVerified;
  useEffect(() => {
    if (!needsCheck) return;
    let inFlight = false;
    let cancelled = false;
    const check = () => {
      if (inFlight) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      inFlight = true;
      void refreshEmailVerification()
        .then((verified) => {
          if (verified && !cancelled) bump((n) => n + 1);
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [needsCheck]);

  if (isLoading) return null;

  // `emailVerified` já cobre Google e Apple, que chegam verificados do
  // provedor — não é preciso inspecionar `providerData` para isentá-los.
  if (!user || user.emailVerified) return children;

  const handleCheck = async () => {
    setChecking(true);
    setMessage(null);
    try {
      const verified = await refreshEmailVerification();
      if (!verified) {
        setStillUnverified(true);
        setMessage("Ainda não confirmamos. Abra o link do e-mail e tente de novo.");
      }
      // Verificado: `reload` + `getIdToken(true)` já correram, e o
      // `onAuthStateChanged` re-renderiza este componente com o passe livre.
    } catch {
      setMessage("Não deu pra checar agora. Tente de novo em instantes.");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setMessage(null);
    try {
      await resendVerificationEmail();
      setMessage("Enviamos de novo. Dá uma olhada na caixa de entrada e no spam.");
    } catch {
      setMessage("Não deu pra reenviar agora. Tente de novo em instantes.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
          <MailCheck className="h-6 w-6 text-primary" />
        </div>

        <h1 className="font-heading text-2xl font-bold leading-tight text-text-main">
          Confirme seu e-mail
        </h1>
        <p className="mt-3 font-body text-sm leading-relaxed text-text-main">
          Mandamos um link de confirmação pra você. Abra ele e volte aqui pra
          continuar montando sua estante. Não achou? Dá uma olhada no spam ou
          na aba Promoções.
        </p>
        {user.email && (
          <p className="mt-2 font-body text-xs leading-relaxed text-text-main">
            Enviado para{" "}
            <span className="font-semibold">{user.email}</span>
          </p>
        )}

        {message && (
          <p
            role="status"
            className="mt-4 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 font-body text-sm leading-relaxed text-text-main"
          >
            {message}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <Button
            size="md"
            onClick={handleCheck}
            disabled={checking}
            className="min-h-11 w-full"
          >
            {checking ? "Checando…" : "Já confirmei"}
          </Button>
          <Button
            variant="line"
            size="md"
            onClick={handleResend}
            disabled={resending}
            className="min-h-11 w-full"
          >
            {resending ? "Enviando…" : "Reenviar e-mail"}
          </Button>
        </div>

        {/* Saída. Ver docblock: sem ela a tela é terminal para quem digitou o
            endereço errado, que é justamente o caso mais provável. */}
        <div className="mt-6 text-center">
          <TextButton
            onClick={() => {
              void signOut(getAuth());
            }}
          >
            Entrar com outra conta
          </TextButton>
        </div>

        {stillUnverified && (
          <p className="mt-4 text-center font-body text-xs leading-relaxed text-text-main">
            O link chega em alguns segundos. Se não aparecer, confira o spam ou
            reenvie acima.
          </p>
        )}
      </div>
    </div>
  );
}
